import Service, { inject as service } from '@ember/service';

import type CacheService from 'later/services/cache';
import type { JsonObject } from 'type-fest';

/**
 * The `utm_source` value OpenAI ad traffic lands with (e.g.
 * `later.com/pricing-v3/?utm_source=chatgpt&...`). Only visitors who arrive
 * from an OpenAI ad should be reported, so conversions no-op unless this
 * source is detected.
 */
const OPENAI_UTM_SOURCE = 'chatgpt';

/**
 * Cache key used to remember that the visitor arrived from an OpenAI ad. The
 * `utm_source` is only present on the landing URL, so we persist it to keep
 * later conversions (e.g. a trial started mid-onboarding, or on a return
 * visit) attributable after the user navigates away from it.
 */
const AD_SOURCE_CACHE_KEY = 'openai_pixel_ad_source';

/**
 * How long an OpenAI ad click stays attributable. Conversions within this
 * window are reported; after it the remembered source expires.
 */
const AD_SOURCE_ATTRIBUTION_DAYS = 30;

/**
 * Standard event names supported by the OpenAI Ads Measurement Pixel that we
 * currently report. Add more from the supported list as new conversions are
 * tracked.
 *
 * @see https://developers.openai.com/ads/supported-events
 */
export type OpenAiPixelEventName = 'lead_created' | 'trial_started';

export interface OpenAiPixelEventOptions {
  /** Reuse the same `event_id` across browser and server events to deduplicate them. */
  event_id?: string;
}

/**
 * Thin wrapper around the OpenAI Ads Measurement Pixel (`oaiq`).
 *
 * The `oaiq` SDK is injected by the backend host page (alongside the Segment
 * snippet), so this service only sends events to the global. All calls no-op
 * gracefully when the pixel is not present (e.g. in tests, or for ghost
 * sessions where the snippet is not rendered).
 *
 * @see https://developers.openai.com/ads/measurement-pixel
 */
export default class OpenAiPixelService extends Service {
  @service declare cache: CacheService;

  /**
   * Sends a conversion event to the OpenAI pixel.
   *
   * @param eventName    One of the supported standard event names, or `custom`.
   * @param eventProps   Event data describing the conversion (include `type` for every event).
   * @param eventOptions Extra delivery options (e.g. `event_id`, `custom_event_name`).
   */
  measure(eventName: OpenAiPixelEventName, eventProps?: JsonObject, eventOptions?: OpenAiPixelEventOptions): void {
    if (typeof window.oaiq === 'undefined') {
      return;
    }

    window.oaiq('measure', eventName, eventProps, eventOptions);
  }

  /**
   * Tracks a `lead_created` conversion (uses the `customer_action` data shape).
   *
   * No-ops unless the visitor originated from an OpenAI ad.
   */
  trackLeadCreated(eventOptions?: OpenAiPixelEventOptions): void {
    if (!this.#isOpenAiAdSource()) {
      return;
    }

    this.measure('lead_created', { type: 'customer_action' }, eventOptions);
  }

  /**
   * Tracks a `trial_started` conversion (uses the `plan_enrollment` data shape).
   *
   * No-ops unless the visitor originated from an OpenAI ad.
   *
   * @param planId Identifier for the plan the trial is for.
   */
  trackTrialStarted(planId: string, eventOptions?: OpenAiPixelEventOptions): void {
    if (!this.#isOpenAiAdSource()) {
      return;
    }

    this.measure('trial_started', { type: 'plan_enrollment', plan_id: planId }, eventOptions);
  }

  /**
   * Captures and remembers the OpenAI ad source when the current URL came from
   * an OpenAI ad.
   *
   * Call this once at app boot, before client-side route transitions can strip
   * the `utm_source` query param from the URL. Conversions that happen later
   * (e.g. an account created or trial started after onboarding navigation)
   * then stay attributable via the persisted value.
   */
  captureAdSource(): void {
    const utmSource = new URLSearchParams(window.location.search).get('utm_source');
    if (utmSource === OPENAI_UTM_SOURCE) {
      this.#rememberAdSource();
    }
  }

  /**
   * Whether the visitor originated from an OpenAI ad.
   *
   * The `utm_source` only lives on the landing URL, so the first time we see
   * it we remember it (in the cache, persisted to localStorage) for the
   * attribution window. Later conversions (e.g. a trial started after
   * onboarding navigation) then stay attributable.
   */
  #isOpenAiAdSource(): boolean {
    const utmSource = new URLSearchParams(window.location.search).get('utm_source');
    if (utmSource === OPENAI_UTM_SOURCE) {
      this.#rememberAdSource();
      return true;
    }

    return this.cache.retrieve<string>(AD_SOURCE_CACHE_KEY) === OPENAI_UTM_SOURCE;
  }

  #rememberAdSource(): void {
    this.cache.add(AD_SOURCE_CACHE_KEY, OPENAI_UTM_SOURCE, {
      expiry: this.cache.expiry(AD_SOURCE_ATTRIBUTION_DAYS, 'days'),
      persist: true
    });
  }
}

declare module '@ember/service' {
  interface Registry {
    'openai-pixel': OpenAiPixelService;
  }
}
